import IngestionStatus from "datasets/pages/tables/types/enums/ingest-status";
import type { IngestResponse } from "datasets/pages/tables/types/interfaces/ingest";
import { AlertCircle, Check, Loader2 } from "lucide-react";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import type { SessionIngest } from "~/pages/manifold-nextgen/components/conversations/useSessionIngests";
import { useIngestStatus } from "~/pages/manifold-nextgen/hooks/useIngestStatus";
import { SessionContextDetailHeader } from "./SessionContextDetailHeader";
import type { SessionContextViewOption } from "./SessionContextDetailHeader";

// The honest, poll-derived state of one ingestion. The database load emits no
// percent or live row count (design D5), so liveness is shown only by an
// indeterminate affordance — never a fabricated progress bar.
type StepState = "done" | "active" | "pending" | "failed";

interface IngestStatusDisplay {
  label: string;
  isLive: boolean;
  isCompleted: boolean;
  isFailed: boolean;
  // Dot state for the "Loading into the database" and terminal steps.
  loadStep: StepState;
  terminalStep: StepState;
  terminalLabel: string;
}

// Maps the raw catalog status to the chip label + stepper state. `undefined`
// (first load, before the poll returns) reads as still-loading, not terminal.
export function ingestStatusDisplay(status: string | undefined): IngestStatusDisplay {
  switch (status) {
    case IngestionStatus.PENDING:
      return {
        label: "Pending",
        isLive: true,
        isCompleted: false,
        isFailed: false,
        loadStep: "pending",
        terminalStep: "pending",
        terminalLabel: "Completed",
      };
    case IngestionStatus.COMPLETED:
      return {
        label: "Completed",
        isLive: false,
        isCompleted: true,
        isFailed: false,
        loadStep: "done",
        terminalStep: "done",
        terminalLabel: "Completed",
      };
    case IngestionStatus.FAILED:
      return {
        label: "Failed",
        isLive: false,
        isCompleted: false,
        isFailed: true,
        loadStep: "failed",
        terminalStep: "failed",
        terminalLabel: "Failed",
      };
    case IngestionStatus.IN_PROGRESS:
    default:
      return {
        label: "Loading",
        isLive: true,
        isCompleted: false,
        isFailed: false,
        loadStep: "active",
        terminalStep: "pending",
        terminalLabel: "Completed",
      };
  }
}

// Last path segment of a URI, for a compact source-file label.
function fileName(uri: string | undefined): string {
  if (!uri) {
    return "—";
  }
  const last = uri.replace(/\/+$/, "").split("/").pop();
  return last && last.length > 0 ? last : uri;
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  // The backend can emit a doubly-terminated UTC offset ("+00:00Z"); drop the
  // stray trailing Z so Date.parse accepts it instead of falling through raw.
  const normalized = value.replace(/([+-]\d{2}:?\d{2})Z$/, "$1");
  const ms = Date.parse(normalized);
  return Number.isNaN(ms)
    ? value
    : new Date(ms).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
}

function rowsLoaded(ingest: IngestResponse | undefined): number | null {
  return ingest?.ingestion_result?.snowflake_result?.row_count ?? null;
}

// Surface whatever failure detail the job carries; never invent one, and never
// hide the failure behind an empty state.
function failureText(ingest: IngestResponse | undefined): string {
  const errors = ingest?.ingestion_result?.metadata_parse_errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors.map((e) => (typeof e === "string" ? e : JSON.stringify(e))).join("\n");
  }
  return "The load failed. Open the ingestion job in the dataset for details.";
}

interface SessionContextIngestDetailProps {
  jobId: string;
  // The session's ingestion list, for the header switcher (peer of the Apps
  // pane's tab switcher). Selecting one focuses its row.
  ingests: SessionIngest[];
  onSelect: (jobId: string) => void;
  onBack: () => void;
  onCollapse: () => void;
}

export const SessionContextIngestDetail = ({
  jobId,
  ingests,
  onSelect,
  onBack,
  onCollapse,
}: SessionContextIngestDetailProps) => {
  const { data: ingest, error, isLoading } = useIngestStatus(jobId);
  const display = ingestStatusDisplay(ingest?.status);
  const rows = rowsLoaded(ingest);

  const views: SessionContextViewOption[] = ingests.map((i) => {
    // Prefer the freshly-polled table for the focused row; every other row uses
    // the table recovered from its own create_ingest result.
    const fqn = i.jobId === jobId ? (ingest?.table_fqn ?? i.tableFqn) : i.tableFqn;
    return { value: i.jobId, label: fqn ? tableName(fqn) : shortId(i.jobId) };
  });

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <SessionContextDetailHeader
        entityName="Ingestions"
        onEntityClick={onBack}
        onBack={onBack}
        onCollapse={onCollapse}
        views={views}
        currentView={jobId}
        onSetView={onSelect}
      />

      {display.isLive && (
        <div className="h-0.5 w-full overflow-hidden" role="progressbar" aria-label="Loading">
          <div className="h-full w-full origin-left bg-primary animate-indeterminate-bar" />
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && !ingest ? (
          <div className="flex flex-col gap-3" role="status" aria-label="Loading ingestion">
            <Skeleton className="h-6 w-1/2 rounded-md" />
            <Skeleton className="h-24 rounded-lg" />
          </div>
        ) : error || !ingest ? (
          <ResourceLoadError error={error} resourceLabel="ingestion" className="py-16" />
        ) : (
          <div className="flex flex-col gap-5">
            <dl className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-6">
                <Field label="Source file">
                  <span className="break-words text-sm text-foreground">
                    {fileName(ingest.source_file_uri)}
                  </span>
                </Field>
                <Field label="Target table">
                  <span className="break-all font-mono text-sm text-foreground">
                    {ingest.table_fqn}
                  </span>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <Field label="Started">
                  <span className="text-sm text-foreground">
                    {formatTimestamp(ingest.last_start_date ?? ingest.created_at)}
                  </span>
                </Field>
                <Field label="Job ID">
                  <span className="break-all font-mono text-xs text-foreground">{jobId}</span>
                </Field>
              </div>
              {display.isCompleted && rows != null && (
                <Field label="Rows loaded">
                  <span className="text-sm text-foreground">{rows.toLocaleString()}</span>
                </Field>
              )}
            </dl>

            <div>
              <h3 className="mb-3 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                Progress
              </h3>
              <ol className="flex flex-col">
                <Step state="done" label="Submitted" sub="The load job was created." last={false} />
                <Step
                  state={display.loadStep}
                  label="Loading into the database"
                  sub="Copying rows into the target table."
                  last={false}
                />
                <Step
                  state={display.terminalStep}
                  label={display.terminalLabel}
                  sub={
                    display.isCompleted && rows != null
                      ? `${rows.toLocaleString()} rows loaded.`
                      : "Reports the final row count when the load finishes."
                  }
                  last
                />
              </ol>
            </div>

            {display.isFailed && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
                <div className="mb-1.5 flex items-center gap-1.5">
                  <AlertCircle className="size-4 text-destructive" aria-hidden />
                  <span className="text-xs font-semibold text-destructive">Load failed</span>
                </div>
                <p className="whitespace-pre-wrap break-words font-mono text-[11px] text-destructive">
                  {failureText(ingest)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function tableName(fqn: string): string {
  return fqn.split(".").pop() || fqn;
}

function shortId(jobId: string): string {
  return jobId.length > 8 ? `${jobId.slice(0, 8)}…` : jobId;
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <dt className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </dt>
    <dd className="min-w-0">{children}</dd>
  </div>
);

const StepDot = ({ state }: { state: StepState }) => {
  if (state === "done") {
    return (
      <span className="flex size-5 items-center justify-center rounded-full bg-green-600 text-white dark:bg-green-500">
        <Check className="size-3" aria-hidden />
      </span>
    );
  }
  if (state === "failed") {
    return (
      <span className="flex size-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground">
        <AlertCircle className="size-3" aria-hidden />
      </span>
    );
  }
  if (state === "active") {
    return (
      <span className="flex size-5 items-center justify-center rounded-full text-primary">
        <Loader2 className="size-4 animate-spin" aria-hidden />
      </span>
    );
  }
  return <span className="size-5 rounded-full border-2 border-border bg-background" />;
};

const Step = ({
  state,
  label,
  sub,
  last,
}: {
  state: StepState;
  label: string;
  sub: string;
  last: boolean;
}) => (
  <li className="flex gap-3">
    <div className="flex flex-none flex-col items-center">
      <StepDot state={state} />
      {!last && <div className="my-1 w-0.5 flex-1 rounded-full bg-border" />}
    </div>
    <div className={cn("flex-1", last ? "pb-1" : "pb-4")}>
      <div className="text-sm font-semibold text-foreground">{label}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>
    </div>
  </li>
);
